import { AccidentIssueWithCacheAnyoneInjuredEnum, AccidentIssueWithCacheWasCrewInvolvedEnum } from '@/models/gen/graphql';
import { DATETIME_FORMAT, TIME_FORMAT } from '@/constants';
import { Datetime, getClasses } from '@/utils';
import { ForwardRefExoticComponent, forwardRef, useCallback } from 'react';

import SimpleTableCell from '@/components/SimpleTable/SimpleTableCell';
import SimpleTableRow from '@/components/SimpleTable/SimpleTableRow';
import { useAccidentHistory } from '@/features/AccidentHistory';
import { useAccidentHistoryDrawer } from '@/features/AccidentHistory/components/AccidentHistoryDrawer';

const AccidentHistoryTableRow = ({ id }: { id: string }, ref: ForwardRefExoticComponent<HTMLDivElement>): JSX.Element => {
  const [{ accident, selected }, setState] = useAccidentHistory(({ accidents, selected }) => ({
    accident: accidents.get(id),
    selected: selected.has(id),
  }));
  const setAccidentHistoryDrawer = useAccidentHistoryDrawer(({ setState }) => setState);

  const onSelect = useCallback(
    (): void =>
      setState((current) => {
        const newSelected = new Map(current.selected);
        if (newSelected.has(id)) newSelected.delete(id);
        else newSelected.set(id, current.accidents.get(id));
        return { ...current, selected: newSelected };
      }),
    [accident, id, setState]
  );
  const onEdit = (): void => {
    setAccidentHistoryDrawer((current) => ({
      ...current,
      show: true,
      data: accident,
    }));
  };

  return (
    <SimpleTableRow id={id} className={getClasses(selected ? 'SELECTED' : undefined)} onDoubleClick={onEdit} ref={ref}>
      <SimpleTableCell.Select checked={selected} onChange={onSelect} />
      <SimpleTableCell name="STATION_TEXT">{accident.stationText || accident.stationText}</SimpleTableCell>
      <SimpleTableCell name="ACCIDENT_DATETIME">{new Datetime(accident.accidentDatetime).format(DATETIME_FORMAT)}</SimpleTableCell>
      <SimpleTableCell name="CREATED_AT">{new Datetime().setAsUTC(accident.createdAt).toLocaleDatetime().frontendDatetime}</SimpleTableCell>
      <SimpleTableCell name="TRIP_SERVICER_IATA_AIRLINE_CODE">{accident.tripServicerIataAirlineCode}</SimpleTableCell>
      <SimpleTableCell name="TRIP_FLIGHT_NAME">
        {accident.tripLoopName || (accident.tripFlightNumber ? `${accident.tripFlightNumber || 0}`.padStart(4, '0') : null)}
      </SimpleTableCell>
      <SimpleTableCell name="TRIP_SCHEDULED">
        {accident.tripId ? new Datetime(accident.tripScheduled).format(TIME_FORMAT) : ''}
      </SimpleTableCell>
      <SimpleTableCell name="VEHICLE_TRACKING_ID">{accident.vehicleTrackingId}</SimpleTableCell>
      <SimpleTableCell name="DRIVER_NAME">{`${accident.driverFirstName} ${accident.driverLastName}`}</SimpleTableCell>
      <SimpleTableCell name="DRIVER_EMPLOYEE_ID">{accident.driverEmployeeId}</SimpleTableCell>
      <SimpleTableCell name="SAFETY_NAME">{`${accident.safetyFirstName} ${accident.safetyLastName}`} </SimpleTableCell>
      <SimpleTableCell
        name="ANYONE_INJURED"
        style={{ color: accident.anyoneInjured === AccidentIssueWithCacheAnyoneInjuredEnum.Yes ? 'var(--bs-danger)' : 'var(--bs-green)' }}
      >
        {accident.anyoneInjured}
      </SimpleTableCell>
      <SimpleTableCell
        name="WAS_CREW_INVOLVED"
        style={{
          color: accident.wasCrewInvolved === AccidentIssueWithCacheWasCrewInvolvedEnum.Yes ? 'var(--bs-danger)' : 'var(--bs-green)',
        }}
      >
        {accident.wasCrewInvolved}
      </SimpleTableCell>
    </SimpleTableRow>
  );
};

export default forwardRef(AccidentHistoryTableRow);
